// ─── Urgency helpers ──────────────────────────────────────────────────────────

export function urgencyColor(daysLeft: number | null): string {
  if (daysLeft === null) return "";
  if (daysLeft <= 0) return "text-term-error";
  if (daysLeft <= 1) return "text-term-error";
  if (daysLeft <= 2) return "text-orange-600";
  if (daysLeft <= 3) return "text-amber-600";
  return "text-term-text-dim";
}

export function urgencyRowClass(daysLeft: number | null): string {
  if (daysLeft === null) return "";
  if (daysLeft <= 1) return "bg-term-error/5 border-l-2 border-l-term-error";
  if (daysLeft <= 2) return "bg-orange-50 border-l-2 border-l-orange-400";
  if (daysLeft <= 3) return "bg-amber-50/60 border-l-2 border-l-amber-400";
  return "";
}

export function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr.includes("T") ? dateStr : dateStr + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor((d.getTime() - now.getTime()) / 86400000);
}

// ─── Fraction formatting ─────────────────────────────────────────────────────

import { APP_LOCALE } from "@/shared/locale";
import { CLEAN_FRACTIONS } from "@/shared/units";

/** Round to N significant digits, dropping trailing zeros. */
export function roundSig(n: number, sig = 3): string {
  if (n === 0) return "0";
  const d = Math.floor(Math.log10(Math.abs(n))) + 1; // number of integer digits
  const decimals = Math.max(0, sig - d);
  const factor = 10 ** decimals;
  const rounded = Math.round(n * factor) / factor;
  return rounded.toLocaleString(APP_LOCALE, { maximumFractionDigits: decimals });
}

/** Format a number using Unicode fractions when possible (e.g. 0.5 → "½", 1.5 → "1½") */
export function formatFraction(q: number): string {
  if (q > 2) return roundSig(q);
  const whole = Math.floor(q);
  const frac = q - whole;
  const match = CLEAN_FRACTIONS.find(([v]) => Math.abs(frac - v) < 0.005);
  if (match) return whole > 0 ? `${whole}${match[1]}` : match[1];
  if (q === Math.floor(q)) return String(q);
  return roundSig(q);
}

/** Format a string quantity using Unicode fractions (e.g. "0.5" → "½") */
export function formatQtyStr(q: string): string {
  const n = parseFloat(q);
  return isNaN(n) ? q : formatFraction(n);
}

// ─── Quantity + unit formatting ─────────────────────────────────────────────

import { displayUnit } from "@/shared/units";

/** Format a quantity with its unit (e.g. 1.5, "kg" → "1½ kg"). No unit humanization. */
export function formatQty(quantity: number, unit: string): string {
  return `${formatFraction(quantity)}\u00A0${displayUnit(unit, quantity)}`;
}

/** Format a quantity range with humanization (g→kg, ml→lt) and pluralization. */
export function formatRange(min: number, max: number | undefined, unit: string): string {
  let u = unit;
  let lo = min;
  let hi = max ?? min;
  // Humanize base units
  if (u === "g" && Math.max(Math.abs(lo), Math.abs(hi)) >= 1000) {
    lo /= 1000;
    hi /= 1000;
    u = "kg";
  } else if (u === "ml" && Math.max(Math.abs(lo), Math.abs(hi)) >= 1000) {
    lo /= 1000;
    hi /= 1000;
    u = "lt";
  }
  if (max == null || Math.abs(hi - lo) < 0.01) return formatQty(lo, u);
  return `${formatFraction(lo)}–${formatFraction(hi)}\u00A0${displayUnit(u, hi)}`;
}
